import { ChangeEventHandler, FC, Fragment } from "react";
import { t } from "i18next";
import { useNavigate, useParams } from "react-router-dom";
import { faTrashCan } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { ButtonPrimary } from "@/shared/components/atoms/ButtonPrimary";
import { InputWithCornerHint } from "../atoms/InputWithCornerHint";
import { ButtonSecondary } from "../atoms/ButtonSecondary";
import { Fee, getFormatedPeriod } from "@/app/fees/fees";
import { TableLoader } from "../molecules/TableLoader";
import { PlusIcon } from "@heroicons/react/20/solid";
import ColumnText from "../molecules/ColumnText";
import { Empty } from "./Empty";

export type TableListCoursesProps = {
  isLoading: boolean;
  onSearchChange: ChangeEventHandler<HTMLInputElement>;
  onEditFee: (fee: Fee) => void;
  search: string | undefined;
  fees: Array<Fee>;
  title: string;
  coachList: Array<any>;
  description: string;
  functionButtonCreate: () => void;
  functionButtonMatriculas: () => void;
  functionOpenModalGrupos: () => void;
  functionButtonMotivos: () => void;
  currentPage: number;
  pageSize: number;
  onDelete: (id: string | number | null) => void;
  onViewUserCourse: (id: string | number | null) => void;
};

const dayTranslations: Record<string, string> = {
  Monday: "Lun",
  Tuesday: "Mar",
  Wednesday: "Mié",
  Thursday: "Jue",
  Friday: "Vie",
  Saturday: "Sáb",
  Sunday: "Dom",
};

export const TableListCourses: FC<TableListCoursesProps> = ({
  isLoading,
  title,
  description,
  onEditFee,
  fees,
  onSearchChange,
  search,
  functionButtonCreate,
  functionButtonMatriculas,
  functionOpenModalGrupos,
  functionButtonMotivos,
  currentPage,
  pageSize,
  onDelete,
  coachList,
  onViewUserCourse,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const startIndex = (currentPage - 1) * pageSize;
  const currentFees = fees.slice(startIndex, startIndex + pageSize);

  return (
    <div className="flex-1 flex-col px-4 sm:px-6 lg:px-6">
      <div className="sm:flex sm:items-center">
        <div className="flex-grow">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            {title}
          </h1>
          <p className="mt-2 text-sm text-gray-700">{description}</p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0"></div>
      </div>

      {/* Divider debajo del texto */}
      <div className="border-b border-gray-300 my-3"></div>

      <div className="sm:flex sm:items-center">
        <div className="sm:flex w-64">
          <InputWithCornerHint
            label={t("general.search")}
            span=""
            id=""
            name=""
            type=""
            placeholder={t("general.search")}
            error={false}
            value={search}
            onChange={onSearchChange}
          />
        </div>
        <div className="mt-8 sm:ml-auto sm:mt-4 sm:flex sm:items-center sm:space-x-4">
          <Fragment>
            <div className="flex items-center">
              <ButtonSecondary
                text={t("lesson.views.groups")}
                onClick={functionOpenModalGrupos}
                size="large"
              />
            </div>
          </Fragment>

          <div className="mr-6 h-6 w-px bg-gray-300" />

          <ButtonSecondary
            text={t("course.views.time")}
            onClick={functionButtonMotivos}
            size="large"
          />
          <ButtonSecondary
            text={t("course.views.holidays")}
            onClick={functionButtonMatriculas}
            size="large"
          />
          <ButtonSecondary
            text={t("course.views.fees")}
            onClick={() => {
              navigate(`/branchs/${id}/events/course-fee`);
            }}
            size="large"
          />

          <div className="mr-6 h-6 w-px bg-gray-300" />

          <button
            onClick={functionButtonCreate}
            type="button"
            className="block rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
          >
            <PlusIcon aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="border-b border-gray-300 mt-5"></div>

      <div className="flex flex-col flex-1">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full px-3 py-2 align-middle sm:px-6 lg:px-8">
            {isLoading ? (
              <TableLoader columns={9} />
            ) : (
              <Fragment>
                {currentFees.length === 0 ? (
                  <Empty
                    texto={t("customer-to-gym-subscription.views.no_courses")}
                    onClick={functionButtonCreate}
                  />
                ) : (
                  <Fragment>
                    <table className="flex-1 min-w-full divide-y divide-gray-300">
                      <thead>
                        <tr>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            {t("general.name")}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            {t("general.description")}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            {t("course.views.days")}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            {t("lesson.views.hour_ini")}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            {t("lesson.views.duration")}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            {t("lesson.views.date_ini")}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            {t("lesson.views.date_end")}
                          </th>
                          <th
                            scope="col"
                            className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >
                            {t("course.views.trainer")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {currentFees.map((course) => (
                          <tr key={course.id}>
                            <ColumnText title={course.name} />
                            <ColumnText
                              title={course.description}
                              className="max-w-60 truncate line-clamp-4"
                            />
                            <ColumnText
                              title={
                                String(course?.days)
                                  ?.split(", ")
                                  .map((day) => dayTranslations[day] || day)
                                  .join(", ") ?? ""
                              }
                            />
                            <ColumnText title={course.time_start} />
                            <ColumnText title={course.duration} />
                            <ColumnText title={course.date_start} />
                            <ColumnText title={course.date_end} />
                            <ColumnText
                              title={
                                coachList.find(
                                  (coach) => coach.id === course.coach_id
                                )?.name ?? "Sin entrenador"
                              }
                            />

                            <td className="relative whitespace-nowrap py-5 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                              <div className="flex items-center justify-end">
                                <a
                                  href="#"
                                  role="button"
                                  className="text-primary hover:text-primary-900 mr-4"
                                  onClick={() => onEditFee(course)}
                                >
                                  {t("general.edit")}
                                  <span className="sr-only"></span>
                                </a>
                                <a
                                  onClick={() => {
                                    onDelete(course.id);
                                  }}
                                  href="#"
                                  className="text-red-600 hover:text-red-900 mr-4"
                                >
                                  <FontAwesomeIcon icon={faTrashCan} />
                                </a>
                                <ButtonPrimary
                                  onClick={() => onViewUserCourse(course.id)}
                                  className="mt-5"
                                  text="Usuarios"
                                  size="large"
                                  type="submit"
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Fragment>
                )}
              </Fragment>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
